import React, { createContext, useContext, useReducer, useEffect, useState, useRef } from 'react';

const CartContext = createContext();

// Actions du panier
const CART_ACTIONS = {
  ADD_ITEM: 'ADD_ITEM',
  REMOVE_ITEM: 'REMOVE_ITEM',
  UPDATE_QUANTITY: 'UPDATE_QUANTITY',
  CLEAR_CART: 'CLEAR_CART',
  ADD_RETURN_TRIP: 'ADD_RETURN_TRIP',
  REMOVE_RETURN_TRIP: 'REMOVE_RETURN_TRIP',
  LOAD_CART: 'LOAD_CART'
};

// Clé de panier namespacing par user pour éviter la contamination cross-utilisateur
const getCartKey = () => {
  try {
    const u = JSON.parse(localStorage.getItem('user') || 'null');
    if (u?.id) return `gowele_cart_${u.id}`;
  } catch {}
  return 'gowele_cart';
};

// Fonction pour sauvegarder le panier dans localStorage
const saveCartToStorage = (cart) => {
  try {
    localStorage.setItem(getCartKey(), JSON.stringify(cart));
  } catch {}
};

// Durée de vie du panier : 30 minutes (standard transport)
const CART_EXPIRY_MS = 30 * 60 * 1000;

// Fonction pour charger le panier depuis localStorage
const loadCartFromStorage = () => {
  try {
    const savedCart = localStorage.getItem(getCartKey());
    if (!savedCart) return { items: [], returnTrips: {} };
    const parsed = JSON.parse(savedCart);
    // Supprimer les articles expirés (> 30 min)
    const now = Date.now();
    const validItems = (parsed.items || []).filter(item => {
      const age = now - new Date(item.date_ajout || 0).getTime();
      return age < CART_EXPIRY_MS;
    });
    if (validItems.length !== (parsed.items || []).length) {
      const cleaned = { ...parsed, items: validItems };
      try { localStorage.setItem(getCartKey(), JSON.stringify(cleaned)); } catch {}
    }
    return { items: validItems, returnTrips: parsed.returnTrips || {} };
  } catch {
    return { items: [], returnTrips: {} };
  }
};

// Reducer du panier avec retour
const cartReducer = (state, action) => {
  let newState;

  switch (action.type) {
    case CART_ACTIONS.LOAD_CART:
      return { ...state, ...action.payload };

    case CART_ACTIONS.ADD_ITEM:
      const existingItem = state.items.find(
        item => item.trajet_id === action.payload.trajet_id && 
                item.siege_numero === action.payload.siege_numero &&
                item.arret_depart === action.payload.arret_depart &&
                item.arret_arrivee === action.payload.arret_arrivee
      );
      
      if (existingItem) {
        newState = {
          ...state,
          items: state.items.map(item =>
            item.id === existingItem.id
              ? { ...item, quantity: item.quantity + 1 }
              : item
          )
        };
      } else {
        newState = {
          ...state,
          items: [...state.items, {
            ...action.payload,
            // Préserver l'id si fourni (nécessaire pour lier le retour à l'aller)
            id: action.payload.id || (Date.now() + Math.random()),
            quantity: 1,
            date_ajout: new Date().toISOString()
          }]
        };
      }
      break;
      
    case CART_ACTIONS.REMOVE_ITEM:
      newState = {
        ...state,
        items: state.items.filter(item => item.id !== action.payload)
      };
      break;
      
    case CART_ACTIONS.UPDATE_QUANTITY:
      newState = {
        ...state,
        items: state.items.map(item =>
          item.id === action.payload.id
            ? { ...item, quantity: Math.max(1, action.payload.quantity) }
            : item
        )
      };
      break;
      
    case CART_ACTIONS.ADD_RETURN_TRIP:
      const { outgoingTripId, returnTrip } = action.payload;
      newState = {
        ...state,
        returnTrips: {
          ...state.returnTrips,
          [outgoingTripId]: returnTrip
        }
      };
      break;
      
    case CART_ACTIONS.REMOVE_RETURN_TRIP:
      const { [action.payload]: removed, ...remainingReturnTrips } = state.returnTrips;
      newState = {
        ...state,
        returnTrips: remainingReturnTrips
      };
      break;
      
    case CART_ACTIONS.CLEAR_CART:
      newState = { items: [], returnTrips: {} };
      break;
      
    default:
      return state;
  }

  // Sauvegarder dans localStorage après chaque modification
  saveCartToStorage(newState);
  return newState;
};

export const CartProvider = ({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, { items: [], returnTrips: {} });
  // { value: number, type: 'pourcentage'|'fixe', name: string } | null
  const [serviceFeeConfig, setServiceFeeConfig] = useState(null);
  const [expiryInfo, setExpiryInfo] = useState({ nearExpiry: false, minutesLeft: null });
  // Keep items ref to avoid stale closure in interval
  const itemsRef = useRef(state.items);
  useEffect(() => { itemsRef.current = state.items; }, [state.items]);

  // ⚠️ N'écrit l'état QUE si la valeur change réellement (comparaison par champ).
  // Sans ce garde, un panier vide déclenchait `setExpiryInfo({...})` (nouvel objet) toutes les
  // 30 s → CartProvider (qui enveloppe toute l'app) se re-rendait indéfiniment sur une page
  // publique au repos. Objectif « meilleurs sites » : ZÉRO re-render sur une page stationnaire.
  const applyExpiry = (next) => setExpiryInfo(prev =>
    (prev.nearExpiry === next.nearExpiry && prev.minutesLeft === next.minutesLeft) ? prev : next
  );

  // Real-time expiry check every 30s - warn when < 5 min left
  useEffect(() => {
    const WARN_MS = 5 * 60 * 1000;
    const check = () => {
      const items = itemsRef.current;
      if (!items || items.length === 0) {
        applyExpiry({ nearExpiry: false, minutesLeft: null });
        return;
      }
      const now = Date.now();
      let earliestExpiry = null;
      items.forEach(item => {
        const exp = new Date(item.date_ajout || 0).getTime() + CART_EXPIRY_MS;
        if (!earliestExpiry || exp < earliestExpiry) earliestExpiry = exp;
      });
      if (earliestExpiry) {
        const msLeft = earliestExpiry - now;
        const minutesLeft = Math.max(0, Math.floor(msLeft / 60000));
        applyExpiry({ nearExpiry: msLeft > 0 && msLeft < WARN_MS, minutesLeft });
      }
    };
    check();
    const interval = setInterval(check, 30000);
    return () => clearInterval(interval);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-check immediately when items change
  useEffect(() => {
    const WARN_MS = 5 * 60 * 1000;
    if (!state.items || state.items.length === 0) {
      applyExpiry({ nearExpiry: false, minutesLeft: null });
      return;
    }
    const now = Date.now();
    let earliestExpiry = null;
    state.items.forEach(item => {
      const exp = new Date(item.date_ajout || 0).getTime() + CART_EXPIRY_MS;
      if (!earliestExpiry || exp < earliestExpiry) earliestExpiry = exp;
    });
    if (earliestExpiry) {
      const msLeft = earliestExpiry - now;
      applyExpiry({ nearExpiry: msLeft > 0 && msLeft < WARN_MS, minutesLeft: Math.max(0, Math.floor(msLeft / 60000)) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.items]);

  // Charger le panier (avec expiration) + frais de service réels au montage
  useEffect(() => {
    const savedCart = loadCartFromStorage();
    dispatch({ type: CART_ACTIONS.LOAD_CART, payload: savedCart });

    const isDev = process.env.NODE_ENV === 'development';
    const apiUrl = isDev ? 'http://localhost:5000/api' : (process.env.REACT_APP_API_URL || 'https://api.gowele.com/api');
    fetch(`${apiUrl}/fees`)
      .then(r => r.json())
      .then(data => {
        if (data.success && data.data?.length) {
          // Priorité : 'panier' > 'billet' > premier disponible
          const fee = data.data.find(f => f.apply_on === 'panier')
                   || data.data.find(f => f.apply_on === 'billet')
                   || data.data[0];
          setServiceFeeConfig({ value: +fee.value, type: fee.fee_type, name: fee.name });
        }
      })
      .catch(() => {});
  }, []);

  const addToCart = (item, options = {}) => {
    const { isReturnTrip = false, outgoingTripId = null } = options;

    if (isReturnTrip && outgoingTripId) {
      // Ajouter un trajet retour associé à un trajet aller
      const retourId = item.id || (Date.now() + Math.random());
      dispatch({
        type: CART_ACTIONS.ADD_RETURN_TRIP,
        payload: {
          outgoingTripId,
          returnTrip: { ...item, id: retourId }
        }
      });
      return retourId;
    } else {
      // Générer l'ID ici pour pouvoir le retourner (le reducer le préserve maintenant)
      const itemId = item.id || (Date.now() + Math.random());
      dispatch({ type: CART_ACTIONS.ADD_ITEM, payload: { ...item, id: itemId } });
      return itemId;
    }
  };

  const removeFromCart = (itemId) => {
    // Si on supprime un trajet aller, supprimer aussi le retour associé
    if (state.returnTrips[itemId]) {
      dispatch({ type: CART_ACTIONS.REMOVE_RETURN_TRIP, payload: itemId });
    }
    dispatch({ type: CART_ACTIONS.REMOVE_ITEM, payload: itemId });
  };

  const updateQuantity = (itemId, quantity) => {
    dispatch({ type: CART_ACTIONS.UPDATE_QUANTITY, payload: { id: itemId, quantity } });
  };

  const clearCart = () => {
    dispatch({ type: CART_ACTIONS.CLEAR_CART });
  };

  const addReturnTrip = (outgoingTripId, returnTrip) => {
    dispatch({ 
      type: CART_ACTIONS.ADD_RETURN_TRIP, 
      payload: { outgoingTripId, returnTrip } 
    });
  };

  const removeReturnTrip = (outgoingTripId) => {
    dispatch({ type: CART_ACTIONS.REMOVE_RETURN_TRIP, payload: outgoingTripId });
  };

  // Calcul du total - parseFloat pour éviter la concaténation string
  const calculateTotal = () => {
    const itemsTotal = state.items.reduce((sum, item) => {
      return sum + (parseFloat(item.prix || item.montant_total || 0) * (item.quantity || 1));
    }, 0);

    const returnTripsTotal = Object.values(state.returnTrips).reduce((sum, trip) => {
      return sum + parseFloat(trip.prix || trip.montant_total || 0);
    }, 0);

    return itemsTotal + returnTripsTotal;
  };

  // Vérifier si un trajet a un retour associé
  const hasReturnTrip = (itemId) => {
    return !!state.returnTrips[itemId];
  };

  // Récupérer le trajet retour d'un trajet aller
  const getReturnTrip = (itemId) => {
    return state.returnTrips[itemId];
  };

  // Calculer les frais de service (depuis l'API, fallback 5%)
  const calculateServiceFees = () => {
    const total = calculateTotal();
    if (!serviceFeeConfig) return total * 0.05;
    return serviceFeeConfig.type === 'fixe'
      ? serviceFeeConfig.value
      : total * serviceFeeConfig.value / 100;
  };

  // Calculer le total final (total + frais)
  const calculateFinalTotal = () => {
    return calculateTotal() + calculateServiceFees();
  };

  const value = {
    items: state.items,
    returnTrips: state.returnTrips,
    total: calculateTotal(),
    serviceFees: calculateServiceFees(),
    serviceFeeConfig,
    finalTotal: calculateFinalTotal(),
    cartExpiryMs: CART_EXPIRY_MS,
    itemCount: state.items.length,
    expiryInfo,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    addReturnTrip,
    removeReturnTrip,
    hasReturnTrip,
    getReturnTrip
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};